import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, ShieldCheckIcon, TriangleAlertIcon, BellIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
import { WaterHero } from '../components/water/WaterHero';
import { WaterChallenge } from '../components/water/WaterChallenge';
import { WaterSolution } from '../components/water/WaterSolution';
import { WaterProduction } from '../components/water/WaterProduction';
import { WaterFinalCTA } from '../components/water/WaterFinalCTA';
// FR lexicon (locked) for the Smart Water / Compteurs intelligents page. EN untouched.
const FR: Record<string, string> = {
  // Comparison rows
  'Manual Quarterly Readings': 'Relevés manuels trimestriels',
  'Real-Time or Photo Capture': 'Temps réel ou saisie photo',
  'Leak Found on Bill': 'Fuite découverte sur la facture',
  'Anomaly Detected Early': 'Anomalie détectée tôt',
  'Anonymous Meter': 'Compteur anonyme',
  'Verified Owner': 'Propriétaire vérifié',
  'Reactive Operations': 'Opérations réactives',
  'Proactive Intervention': 'Intervention proactive',
  // Detection→Action flow
  'Property & Meter': 'Propriété et compteur',
  'Consumption Data': 'Données de consommation',
  'ATNA Detection': 'Détection ATNA',
  'Notification & Action': 'Notification et action',
  Resolution: 'Résolution',
  // Compliance badges + proof attributes
  'Law 25 Ready': 'Conforme Loi 25',
  'Canadian Hosted': 'Hébergé au Canada',
  'Auditable Actions': 'Actions traçables',
  'Live Production Smart Water Deployment': 'Déploiement des Compteurs d’eau ATNA en production',
  'Verified Identity Infrastructure': "Infrastructure d'identité vérifiée",
  'Operational Monitoring': 'Surveillance opérationnelle',
  // Benefits — Property Owner
  'Early leak detection': 'Détection précoce des fuites',
  'Lower water costs': "Coûts d'eau réduits",
  'Property-level insights': 'Visibilité par propriété',
  'Personalized recommendations': 'Recommandations personnalisées',
  'Multiple property management': 'Gestion de plusieurs propriétés',
  // Benefits — City / Utility
  'Reduced disputes': 'Litiges réduits',
  'Conservation goals': 'Objectifs de conservation',
  'Automated workflows': 'Flux de travail automatisés',
  'Fewer manual readings': 'Moins de relevés manuels',
  'Better operational visibility': 'Meilleure visibilité opérationnelle',
  // Beyond water resources
  Water: 'Eau',
  Gas: 'Gaz',
  Electricity: 'Électricité',
  'Environmental Monitoring': 'Surveillance environnementale',
  Utilities: 'Services publics',
  'Smart Infrastructure': 'Infrastructure intelligente'
};
// Research cards (label + description) for "Built on Real Infrastructure".
const RESEARCH_FR: Record<string, {
  label: string;
  desc: string;
}> = {
  'Leak Reduction Potential': {
    label: 'Potentiel de réduction des fuites',
    desc: 'Des notifications ciblées de compteurs intelligents ont permis de réduire les fuites en aval du compteur jusqu’à 89 %.*'
  },
  'Faster Repair Response': {
    label: 'Réparation plus rapide',
    desc: 'La visibilité en ligne de la consommation a permis de réduire les délais de réparation des fuites jusqu’à 34 %.*'
  },
  'Water Consumption Reduction': {
    label: "Réduction de la consommation d'eau",
    desc: "La recherche montre que la visibilité en temps réel de la consommation peut réduire l'usage résidentiel de l'eau.*"
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  trad: 'Manual Quarterly Readings',
  bciti: 'Real-Time or Photo Capture'
}, {
  trad: 'Leak Found on Bill',
  bciti: 'Anomaly Detected Early'
}, {
  trad: 'Anonymous Meter',
  bciti: 'Verified Owner'
}, {
  trad: 'Reactive Operations',
  bciti: 'Proactive Intervention'
}];
const ACTION_FLOW = [{
  label: 'Verified Owner'
}, {
  label: 'Property & Meter'
}, {
  label: 'Consumption Data'
}, {
  label: 'ATNA Detection',
  highlight: true
}, {
  label: 'Notification & Action',
  highlight: true
}, {
  label: 'Resolution'
}];
const PROOF_ATTRIBUTES = ['Live Production Smart Water Deployment', 'Verified Identity Infrastructure', 'Operational Monitoring', 'Law 25 Ready', 'Canadian Hosted', 'Auditable Actions'];
const BEYOND_RESOURCES = ['Water', 'Gas', 'Electricity', 'Environmental Monitoring', 'Utilities', 'Smart Infrastructure'];
export function SmartWater() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const researchLabel = (en: string) => lang === 'FR' ? RESEARCH_FR[en]?.label ?? en : en;
  const researchDesc = (en: string, desc: string) => lang === 'FR' ? RESEARCH_FR[en]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Smart Water'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8720" data-mp="oiw1olfv7f02">
      <Header data-id="element-8721" data-mp="okqn00v1t94o" />

      <main className="pt-[120px]" data-id="element-8722" data-mp="sk992zylvq93">
        {/* SECTION 1: HERO */}
        <WaterHero data-id="element-8723" data-mp="sjzgr5q39708" />

        {/* SECTION: LE DÉFI */}
        <section className="relative bg-white overflow-hidden pt-10 xl:pt-0 pb-20" data-id="element-8724" data-mp="xistgvxjwtzc">
          <WaterChallenge data-id="element-8725" data-mp="12ioow6s6nd3a" />
        </section>

        {/* SECTION: LA SOLUTION */}
        <section className="relative bg-white overflow-hidden pt-10 xl:pt-0 pb-20 border-t border-[#EEF0F4]" data-id="element-8726" data-mp="12gkovzxemy8a">
          <WaterSolution data-id="element-8727" data-mp="349s3t893vdtn" />
        </section>

        {/* SECTION 6: PROVEN IN PRODUCTION — Casselman */}
        <section id="casselman-proof" className="relative bg-white overflow-hidden pt-10 xl:pt-0 pb-16 border-t border-[#EEF0F4] scroll-mt-24" data-id="element-8728" data-mp="398le6rt3iopl">
          <WaterProduction data-id="element-8729" data-mp="31a0as0rryqbe" />
        </section>

        {/* SECTION 7: BEYOND WATER — discreet product-expansion band */}


        {/* FINAL CTA */}
        <WaterFinalCTA data-id="element-8730" data-mp="3b7eer0wihmmt" />
      </main>

      <Footer data-id="element-8731" data-mp="3b7o3kl5tlnqx" />
    </div>;
}